/**
 * LoginBrandingEditor - 登录界面品牌配置编辑器
 * 超级管理员可以配置登录页面的 Logo、标题、版本、背景图等
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Trash2, Image, Type, GripVertical, Loader2, Save, Eye, AlertCircle } from 'lucide-react';
import api, { getToken } from '../api';
import { API_BASE_URL } from '../constants';
import { getLogos, MAX_LOGOS, writeBrandingCache } from '../utils/branding';
import ConfirmModal from './ConfirmModal';

export default function LoginBrandingEditor({ open, onClose }) {
  // 配置状态
  const [config, setConfig] = useState({
    logos: [], // 最多 MAX_LOGOS 个，登录页从左到右并排显示
    appName: 'Cineart Canvas',
    version: 'Studio 1.0',
    topRightText: '',
    organizations: [],
    backgroundImages: [],
    cornerLogo: '', // 🆕 右角标
    cornerLogoColor: '#ffffff', // 右角标颜色
    cornerLogoOpacity: 0.3 // 右角标透明度 (0-1)
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false); // 🆕 成功弹窗
  
  // 上传状态
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingCornerLogo, setUploadingCornerLogo] = useState(false); // 🆕
  
  // Refs
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);
  const cornerLogoInputRef = useRef(null); // 🆕
  
  // 加载配置
  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open]);
  
  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/api/settings/login-branding');
      if (data) {
        // 老配置只有单个 logo 字符串，统一成数组再进 state
        setConfig(prev => ({ ...prev, ...data, logos: getLogos(data) }));
      }
    } catch (err) {
      setError('加载配置失败: ' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };
  
  // 保存配置
  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // 同时写回老字段 logo = 第一个 —— 领码页和还没刷新的老前端只认它，
      // 少了这一份镜像，保存完那些地方的 Logo 会直接消失。
      const payload = { ...config, logo: config.logos[0] || '' };
      await api.put('/api/settings/login-branding', payload);
      // 📌 软件名改了即时同步：更新标签页标题 + 回写缓存，不必刷新页面
      if (config.appName?.trim()) document.title = config.appName.trim();
      writeBrandingCache(payload);
      setShowSuccessModal(true); // 🆕 显示成功弹窗
    } catch (err) {
      setError('保存失败: ' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };
  
  // 上传 Logo（追加到列表末尾，最多 MAX_LOGOS 个）
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (config.logos.length >= MAX_LOGOS) {
      setError(`最多只能上传 ${MAX_LOGOS} 个 Logo，请先删掉一个`);
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    
    // 验证文件大小 (最大 500KB)
    if (file.size > 500 * 1024) {
      setError('Logo 文件过大，请选择 500KB 以内的图片');
      return;
    }
    
    setUploadingLogo(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/api/upload?type=logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('上传失败');
      }
      
      const data = await response.json();
      setConfig(prev => ({ ...prev, logos: [...prev.logos, data.url].slice(0, MAX_LOGOS) }));
    } catch (err) {
      setError('Logo 上传失败: ' + (err.message || '未知错误'));
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };
  
  // 上传背景图
  const handleBgUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    
    // 验证文件大小 (最大 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('背景图文件过大，请选择 5MB 以内的图片');
      return;
    }
    
    setUploadingBg(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/api/upload?type=login-bg`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('上传失败');
      }
      
      const data = await response.json();
      setConfig(prev => ({
        ...prev,
        backgroundImages: [...prev.backgroundImages, data.url]
      }));
    } catch (err) {
      setError('背景图上传失败: ' + (err.message || '未知错误'));
    } finally {
      setUploadingBg(false);
      if (bgInputRef.current) {
        bgInputRef.current.value = '';
      }
    }
  };
  
  // 删除第 index 个 Logo
  const handleRemoveLogo = (index) => {
    setConfig(prev => ({ ...prev, logos: prev.logos.filter((_, i) => i !== index) }));
  };
  
  // 🆕 上传右角标
  const handleCornerLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // 验证文件类型（支持 SVG 和常规图片）
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件（支持 SVG/PNG/JPG）');
      return;
    }
    
    // 验证文件大小 (最大 1MB)
    if (file.size > 1024 * 1024) {
      setError('右角标文件过大，请选择 1MB 以内的图片');
      return;
    }
    
    setUploadingCornerLogo(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/api/upload?type=corner-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('上传失败');
      }
      
      const data = await response.json();
      setConfig(prev => ({ ...prev, cornerLogo: data.url }));
    } catch (err) {
      setError('右角标上传失败: ' + (err.message || '未知错误'));
    } finally {
      setUploadingCornerLogo(false);
      if (cornerLogoInputRef.current) {
        cornerLogoInputRef.current.value = '';
      }
    }
  };
  
  // 🆕 删除右角标
  const handleRemoveCornerLogo = () => {
    setConfig(prev => ({ ...prev, cornerLogo: '' }));
  };
  
  // 删除背景图
  const handleRemoveBg = (index) => {
    setConfig(prev => ({
      ...prev,
      backgroundImages: prev.backgroundImages.filter((_, i) => i !== index)
    }));
  };
  
  // 添加组织
  const handleAddOrganization = () => {
    setConfig(prev => ({
      ...prev,
      organizations: [...prev.organizations, { logo: '', name: '' }]
    }));
  };
  
  // 删除组织
  const handleRemoveOrganization = (index) => {
    setConfig(prev => ({
      ...prev,
      organizations: prev.organizations.filter((_, i) => i !== index)
    }));
  };
  
  // 更新组织名称
  const handleOrganizationNameChange = (index, name) => {
    setConfig(prev => ({
      ...prev,
      organizations: prev.organizations.map((org, i) => 
        i === index ? { ...org, name } : org
      )
    }));
  };
  
  // 上传组织 Logo
  const handleOrganizationLogoUpload = async (index, file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      setError('请选择图片文件');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      setError('组织 Logo 文件过大，请选择 2MB 以内的图片');
      return;
    }
    
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_BASE_URL}/api/upload?type=org-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('上传失败');
      }
      
      const data = await response.json();
      setConfig(prev => ({
        ...prev,
        organizations: prev.organizations.map((org, i) => 
          i === index ? { ...org, logo: data.url } : org
        )
      }));
    } catch (err) {
      setError('组织 Logo 上传失败: ' + (err.message || '未知错误'));
    }
  };
  
  // 预览登录页面
  const handlePreview = () => {
    window.open('/login', '_blank');
  };
  
  if (!open) return null;
  
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <Image size={24} style={{ color: '#3b82f6' }} />
            <h2 style={styles.title}>登录界面配置</h2>
          </div>
          <div style={styles.headerRight}>
            <button onClick={handlePreview} style={styles.previewBtn} title="预览登录页面">
              <Eye size={18} />
              <span>预览</span>
            </button>
            <button onClick={onClose} style={styles.closeBtn}>
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingBox}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
              <span>加载中...</span>
            </div>
          ) : (
            <>
              {/* 错误提示 */}
              {error && (
                <div style={styles.errorBox}>
                  <AlertCircle size={16} />
                  <span>{error}</span>
                  <button onClick={() => setError('')} style={styles.dismissBtn}>
                    <X size={14} />
                  </button>
                </div>
              )}
              
              {/* 配置区域 */}
              <div style={styles.configGrid}>
                {/* 左侧：Logo 和文字配置 */}
                <div style={styles.configSection}>
                  <h3 style={styles.sectionTitle}>
                    <Type size={16} />
                    品牌信息
                  </h3>
                  
                  {/* Logo 上传 —— 最多 3 个，登录页按这里的顺序从左到右并排 */}
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>
                      Logo（最多 {MAX_LOGOS} 个，支持 PNG/SVG，单个最大 500KB）
                    </label>
                    <div style={styles.logoUploadArea}>
                      <div style={styles.logoRow}>
                        {config.logos.map((url, index) => (
                          <div key={`${url}-${index}`} style={styles.logoSlot}>
                            <img
                              src={url}
                              alt={`Logo ${index + 1}`}
                              style={styles.logoSlotImg}
                              onError={(e) => {
                                e.target.alt = 'Logo 加载失败';
                                e.target.style.opacity = '0.3';
                              }}
                            />
                            <button
                              onClick={() => handleRemoveLogo(index)}
                              style={styles.logoRemove}
                              title="删除这个 Logo"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                        {config.logos.length < MAX_LOGOS && (
                          <div
                            style={styles.logoAddSlot}
                            onClick={() => logoInputRef.current?.click()}
                          >
                            {uploadingLogo ? (
                              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <>
                                <Upload size={24} />
                                <span>{config.logos.length ? '添加 Logo' : '点击上传 Logo'}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/png,image/svg+xml"
                        onChange={handleLogoUpload}
                        style={{ display: 'none' }}
                      />
                      {config.logos.length > 0 && (
                        <p style={styles.logoHint}>
                          登录页从左到右并排显示，间距 2px；点右上角 × 删除。
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* 软件名称 */}
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>软件名称</label>
                    <input
                      type="text"
                      value={config.appName}
                      onChange={(e) => setConfig(prev => ({ ...prev, appName: e.target.value }))}
                      placeholder="例如：Cineart Canvas"
                      style={styles.input}
                    />
                  </div>
                  
                  {/* 版本号 */}
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>版本号</label>
                    <input
                      type="text"
                      value={config.version}
                      onChange={(e) => setConfig(prev => ({ ...prev, version: e.target.value }))}
                      placeholder="例如：Studio 1.0"
                      style={styles.input}
                    />
                  </div>
                  
                  {/* 右上角文字 */}
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>右上角文字（品牌标识）</label>
                    <input
                      type="text"
                      value={config.topRightText}
                      onChange={(e) => setConfig(prev => ({ ...prev, topRightText: e.target.value }))}
                      placeholder="例如：Your Brand"
                      style={styles.input}
                    />
                  </div>
                  
                  {/* 🆕 右角标 */}
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>右角标（支持 SVG/PNG，最大 1MB）</label>
                    <p style={{ ...styles.sectionDesc, marginTop: 4, marginBottom: 12 }}>
                      显示在登录界面右下角组织列表上方，适合放置品牌标识或手写字体
                    </p>
                    <div style={styles.logoUploadArea}>
                      {config.cornerLogo ? (
                        <div style={styles.logoPreview}>
                          <img 
                            src={config.cornerLogo} 
                            alt="右角标" 
                            style={{
                              ...styles.logoImg,
                              opacity: config.cornerLogoOpacity || 0.3,
                              filter: 
                                config.cornerLogoColor === '#ffffff' ? 'brightness(0) invert(1)' 
                                : config.cornerLogoColor === '#9ca3af' ? 'brightness(0) invert(0.6)'
                                : config.cornerLogoColor === '#000000' ? 'brightness(0) invert(0)'
                                : config.cornerLogoColor === '#ef4444' ? 'brightness(0) saturate(100%) invert(38%) sepia(94%) saturate(3498%) hue-rotate(345deg) brightness(96%) contrast(92%)'
                                : config.cornerLogoColor === '#fbbf24' ? 'brightness(0) saturate(100%) invert(79%) sepia(61%) saturate(555%) hue-rotate(359deg) brightness(102%) contrast(97%)'
                                : config.cornerLogoColor === '#3b82f6' ? 'brightness(0) saturate(100%) invert(46%) sepia(98%) saturate(1677%) hue-rotate(207deg) brightness(100%) contrast(93%)'
                                : config.cornerLogoColor === '#10b981' ? 'brightness(0) saturate(100%) invert(66%) sepia(38%) saturate(3260%) hue-rotate(118deg) brightness(91%) contrast(86%)'
                                : 'brightness(0) invert(1)'
                            }}
                            onError={(e) => {
                              e.target.alt = '右角标加载失败';
                              e.target.style.opacity = '0.3';
                            }}
                          />
                          <button onClick={handleRemoveCornerLogo} style={styles.removeBtn} title="移除右角标">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ) : (
                        <div 
                          style={styles.uploadPlaceholder}
                          onClick={() => cornerLogoInputRef.current?.click()}
                        >
                          {uploadingCornerLogo ? (
                            <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <>
                              <Upload size={24} />
                              <span>点击上传右角标</span>
                            </>
                          )}
                        </div>
                      )}
                      <input
                        ref={cornerLogoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleCornerLogoUpload}
                        style={{ display: 'none' }}
                      />
                      {config.cornerLogo && (
                        <button 
                          onClick={() => cornerLogoInputRef.current?.click()}
                          style={styles.changeBtn}
                          disabled={uploadingCornerLogo}
                        >
                          {uploadingCornerLogo ? '上传中...' : '更换右角标'}
                        </button>
                      )}
                    </div>
                    
                    {/* 颜色和透明度控制 */}
                    {config.cornerLogo && (
                      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* 颜色选择 */}
                        <div>
                          <label style={styles.label}>显示颜色</label>
                          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#ffffff' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#ffffff',
                                border: config.cornerLogoColor === '#ffffff' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="白色"
                            />
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#9ca3af' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#9ca3af',
                                border: config.cornerLogoColor === '#9ca3af' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="灰色"
                            />
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#000000' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#000000',
                                border: config.cornerLogoColor === '#000000' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="黑色"
                            />
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#ef4444' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#ef4444',
                                border: config.cornerLogoColor === '#ef4444' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="红色"
                            />
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#fbbf24' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#fbbf24',
                                border: config.cornerLogoColor === '#fbbf24' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="黄色"
                            />
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#3b82f6' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#3b82f6',
                                border: config.cornerLogoColor === '#3b82f6' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="蓝色"
                            />
                            <button
                              onClick={() => setConfig(prev => ({ ...prev, cornerLogoColor: '#10b981' }))}
                              style={{
                                ...styles.colorBtn,
                                background: '#10b981',
                                border: config.cornerLogoColor === '#10b981' ? '3px solid #3b82f6' : '2px solid rgba(255,255,255,0.2)'
                              }}
                              title="绿色"
                            />
                          </div>
                        </div>
                        
                        {/* 透明度滑块 */}
                        <div>
                          <label style={styles.label}>透明度：{Math.round((config.cornerLogoOpacity || 0.3) * 100)}%</label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Math.round((config.cornerLogoOpacity || 0.3) * 100)}
                            onChange={(e) => setConfig(prev => ({ ...prev, cornerLogoOpacity: parseInt(e.target.value) / 100 }))}
                            style={styles.rangeSlider}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* 相关机构或组织名 */}
                  <div style={styles.fieldGroup}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <label style={styles.label}>相关机构或组织名</label>
                      <button 
                        type="button"
                        onClick={handleAddOrganization}
                        style={styles.addOrgBtn}
                      >
                        + 添加组织
                      </button>
                    </div>
                    <p style={{ ...styles.sectionDesc, marginTop: 0, marginBottom: 12 }}>
                      在登录界面底部展示相关机构的 Logo 和名称，一字排开
                    </p>
                    
                    {config.organizations.length === 0 && (
                      <div style={styles.emptyHint}>
                        暂无组织，点击"添加组织"开始配置
                      </div>
                    )}
                    
                    {config.organizations.map((org, index) => (
                      <div key={index} style={styles.orgItem}>
                        <div style={styles.orgLogoSection}>
                          {org.logo ? (
                            <div style={styles.orgLogoPreview}>
                              <img src={org.logo} alt="组织Logo" style={styles.orgLogoImg} />
                              <button
                                type="button"
                                onClick={() => {
                                  setConfig(prev => ({
                                    ...prev,
                                    organizations: prev.organizations.map((o, i) => 
                                      i === index ? { ...o, logo: '' } : o
                                    )
                                  }));
                                }}
                                style={styles.orgLogoRemove}
                                title="删除Logo"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ) : (
                            <label style={styles.orgLogoUpload}>
                              <Upload size={16} />
                              <span>上传Logo</span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleOrganizationLogoUpload(index, file);
                                    e.target.value = '';
                                  }
                                }}
                                style={{ display: 'none' }}
                              />
                            </label>
                          )}
                        </div>
                        <input
                          type="text"
                          value={org.name}
                          onChange={(e) => handleOrganizationNameChange(index, e.target.value)}
                          placeholder="组织名称"
                          style={styles.orgNameInput}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveOrganization(index)}
                          style={styles.orgRemoveBtn}
                          title="删除组织"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* 右侧：背景图配置 */}
                <div style={styles.configSection}>
                  <h3 style={styles.sectionTitle}>
                    <Image size={16} />
                    背景图片（随机显示）
                  </h3>
                  
                  <p style={styles.sectionDesc}>
                    建议尺寸：21:9（如 1920×823 或 1680×720），JPG 格式，单张 500KB-1MB 最佳
                  </p>
                  
                  {/* 背景图列表 */}
                  <div style={styles.bgList}>
                    {config.backgroundImages.map((url, index) => (
                      <div key={url} style={styles.bgItem}>
                        <div style={styles.bgItemLeft}>
                          <div style={styles.bgItemHandle}>
                            <GripVertical size={16} />
                          </div>
                          <span style={styles.bgIndex}>#{index + 1}</span>
                        </div>
                        <img src={url} alt={`背景图 ${index + 1}`} style={styles.bgThumb} />
                        <button 
                          onClick={() => handleRemoveBg(index)} 
                          style={styles.bgRemoveBtn}
                          title="删除"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    
                    {/* 添加背景图按钮 */}
                    <div 
                      style={styles.bgAddBtn}
                      onClick={() => bgInputRef.current?.click()}
                    >
                      {uploadingBg ? (
                        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <>
                          <Upload size={24} />
                          <span>添加背景图</span>
                          <span style={styles.bgCount}>
                            已上传 {config.backgroundImages.length} 张
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <input
                    ref={bgInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleBgUpload}
                    style={{ display: 'none' }}
                  />
                  
                  {config.backgroundImages.length === 0 && (
                    <p style={styles.noBgHint}>
                      未配置背景图时，页面将显示默认的渐变效果
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelBtn}>
            取消
          </button>
          <button 
            onClick={handleSave} 
            style={styles.saveBtn}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                保存中...
              </>
            ) : (
              <>
                <Save size={16} />
                保存配置
              </>
            )}
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* 🆕 成功弹窗 */}
      {showSuccessModal && (
        <ConfirmModal
          title="配置已保存"
          message="登录界面配置已成功保存！退出登录刷新页面即可查看效果。"
          confirmText="我知道了"
          cancelText=""
          onConfirm={() => setShowSuccessModal(false)}
          onCancel={() => setShowSuccessModal(false)}
        />
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(8px)',
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  modal: {
    width: '900px',
    maxWidth: '95vw',
    maxHeight: '90vh',
    background: '#1a1a1d',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#fff',
  },
  
  previewBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '8px',
    color: '#60a5fa',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  closeBtn: {
    background: 'transparent',
    border: 'none',
    padding: '8px',
    cursor: 'pointer',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
  },
  
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '60px',
    color: '#94a3b8',
  },
  
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    borderRadius: '8px',
    color: '#f87171',
    fontSize: '13px',
    marginBottom: '16px',
  },
  
  successBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    borderRadius: '8px',
    color: '#4ade80',
    fontSize: '13px',
    marginBottom: '16px',
  },
  
  dismissBtn: {
    marginLeft: 'auto',
    background: 'transparent',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
  },
  
  configGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '32px',
  },
  
  configSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: '#e2e8f0',
    paddingBottom: '12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  
  sectionDesc: {
    margin: 0,
    fontSize: '12px',
    color: '#64748b',
  },
  
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#94a3b8',
  },
  
  input: {
    padding: '12px 14px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  
  logoUploadArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  // ── 多 Logo 编辑（最多 3 个） ──
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },

  logoSlot: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '14px 16px',
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    flexShrink: 0,
  },

  logoSlotImg: {
    // ⚠️ 必须写死 height，不能只给 maxHeight：Illustrator 导出的 SVG 常常只有
    // viewBox 没有 width/height，这种"无固有尺寸"的图放进 flex 容器里、又只有
    // max-* 约束时，Chrome 会把它算成 0×0，Logo 直接看不见。
    height: '48px',
    width: 'auto',
    maxWidth: '120px',
    objectFit: 'contain',
  },

  // 删除按钮压在 Logo 右上角（和下面机构 Logo 那套一致）
  logoRemove: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    background: '#ef4444',
    border: 'none',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    cursor: 'pointer',
    padding: 0,
  },

  logoAddSlot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '18px 22px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '2px dashed rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    color: '#64748b',
    fontSize: '12px',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.2s',
  },

  logoHint: {
    margin: 0,
    fontSize: '12px',
    color: '#64748b',
  },

  logoPreview: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 24px',
    background: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  
  logoImg: {
    // 同 logoSlotImg：写死 height，避免无固有尺寸的 SVG 在 flex 里塌成 0×0
    height: '60px',
    width: 'auto',
    maxWidth: '200px',
    objectFit: 'contain',
  },
  
  removeBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'rgba(239, 68, 68, 0.8)',
    border: 'none',
    borderRadius: '6px',
    padding: '6px',
    cursor: 'pointer',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
  },
  
  uploadPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '32px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '2px dashed rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  changeBtn: {
    alignSelf: 'flex-start',
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    color: '#94a3b8',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  bgList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  
  bgItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    transition: 'all 0.2s',
  },
  
  bgItemLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '60px',
  },
  
  bgItemHandle: {
    color: '#475569',
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
  },
  
  bgThumb: {
    flex: 1,
    width: 'auto',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  bgIndex: {
    fontSize: '13px',
    color: '#94a3b8',
    fontWeight: '500',
    minWidth: '30px',
  },
  
  bgRemoveBtn: {
    background: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 8px',
    cursor: 'pointer',
    color: '#f87171',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
  },
  
  bgAddBtn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '24px',
    background: 'rgba(0, 0, 0, 0.3)',
    border: '2px dashed rgba(255, 255, 255, 0.15)',
    borderRadius: '12px',
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  bgCount: {
    fontSize: '11px',
    color: '#475569',
  },
  
  noBgHint: {
    margin: '8px 0 0',
    fontSize: '12px',
    color: '#475569',
    fontStyle: 'italic',
  },
  
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  
  cancelBtn: {
    padding: '10px 20px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  addOrgBtn: {
    background: 'rgba(59, 130, 246, 0.1)',
    border: '1px solid rgba(59, 130, 246, 0.3)',
    borderRadius: '6px',
    padding: '6px 12px',
    color: '#60a5fa',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  emptyHint: {
    padding: '24px',
    textAlign: 'center',
    color: '#64748b',
    fontSize: '13px',
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '8px',
    border: '1px dashed rgba(255,255,255,0.1)',
  },
  
  orgItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    marginBottom: '8px',
  },
  
  orgLogoSection: {
    flexShrink: 0,
  },
  
  orgLogoPreview: {
    position: 'relative',
    width: '48px',
    height: '48px',
  },
  
  orgLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '6px',
    background: 'rgba(255,255,255,0.05)',
  },
  
  orgLogoRemove: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    background: '#ef4444',
    border: 'none',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    cursor: 'pointer',
    padding: 0,
  },
  
  orgLogoUpload: {
    width: '48px',
    height: '48px',
    borderRadius: '6px',
    border: '2px dashed rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.02)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    color: '#64748b',
    fontSize: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  orgNameInput: {
    flex: 1,
    background: 'rgba(15, 23, 42, 0.6)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    padding: '8px 12px',
    color: '#e2e8f0',
    fontSize: '13px',
    outline: 'none',
  },
  
  orgRemoveBtn: {
    flexShrink: 0,
    background: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    borderRadius: '6px',
    padding: '8px',
    cursor: 'pointer',
    color: '#f87171',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s',
  },
  
  saveBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  // 🎨 右角标颜色按钮
  colorBtn: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  
  // 🎚️ 透明度滑块
  rangeSlider: {
    width: '100%',
    height: '6px',
    borderRadius: '3px',
    outline: 'none',
    background: 'linear-gradient(to right, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.6))',
    cursor: 'pointer',
    marginTop: '8px',
  },
};
